import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Mail, Phone, MapPin, Building } from 'lucide-react';

// Link-uri interne crawlabile spre paginile de oraș (SEO - descoperire + indexare)
const TOP_CITIES = [
  ['București', 'bucuresti'], ['Cluj-Napoca', 'cluj-napoca'], ['Timișoara', 'timisoara'],
  ['Iași', 'iasi'], ['Constanța', 'constanta'], ['Craiova', 'craiova'],
  ['Brașov', 'brasov'], ['Galați', 'galati'], ['Ploiești', 'ploiesti'],
  ['Oradea', 'oradea'], ['Pitești', 'pitesti'], ['Bacău', 'bacau']
];

const Footer = ({ onContactClick }) => {
  const [showContactModal, setShowContactModal] = useState(false);
  const companyData = {
    name: 'BITCRAFTERS SRL',
    cui: 'RO49900684',
    registrationNumber: 'J2024007383407',
    address: 'Str. Fratii Golescu 4',
    city: 'București',
    county: 'Sector 1',
    phone: '+40741909674',
    email: 'contact@cf-extras.ro',
    schedule: 'Luni - Vineri: 09:00 - 17:00'
  };

  // Funcție pentru deschidere modal (poate fi apelată din exterior prin prop)
  const handleContactClick = () => {
    setShowContactModal(true);
    if (onContactClick) onContactClick(); // Apelează callback-ul dacă există
  };

  // Ascultă pentru event custom de deschidere modal din Header
  useEffect(() => {
    const handleOpenContactModal = () => {
      setShowContactModal(true);
    };

    window.addEventListener('openContactModal', handleOpenContactModal);
    return () => {
      window.removeEventListener('openContactModal', handleOpenContactModal);
    };
  }, []);

  return (
    <footer id="footer" className="bg-gray-900 text-white py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/logo-alb.png"
            alt="CF-Extras.ro - Extrase Carte Funciară Online"
            className="h-24 sm:h-28 lg:h-32 object-contain"
            width="612"
            height="408"
          />
        </div>

        {/* Extras CF pe orașe - link-uri interne */}
        <div className="mb-10 text-center">
          <h4 className="font-bold text-lg mb-4">Extras carte funciară pe orașe</h4>
          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-gray-400">
            {TOP_CITIES.map(([name, slug]) => (
              <li key={slug}>
                <Link to={`/extras-carte-funciara/${slug}/`} className="hover:text-white transition-colors">
                  Extras CF {name}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/extras-carte-funciara/" className="text-emerald-400 hover:text-white font-semibold transition-colors">
                Toate orașele →
              </Link>
            </li>
          </ul>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Column 1: Links */}
          <div>
            <h4 className="font-bold text-lg mb-4">Informații Legale</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link to="/ghid/" className="hover:text-white transition-colors">
                  Ghiduri carte funciară
                </Link>
              </li>
              <li>
                <Link to="/politica-confidentialitate/" className="hover:text-white transition-colors">
                  Politica de confidențialitate
                </Link>
              </li>
              <li>
                <Link to="/termeni-conditii/" className="hover:text-white transition-colors">
                  Termeni și condiții
                </Link>
              </li>
              <li>
                <button onClick={handleContactClick} className="hover:text-white transition-colors">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Column 2: SOL & SAL */}
          <div className="flex flex-col items-center justify-center">
            <div className="space-y-3 max-w-[200px]">
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="block">
                <img src="/sol.png" alt="SOL - Soluționarea Online a Litigiilor" className="w-full h-auto hover:opacity-80 transition-opacity rounded" width="764" height="217" />
              </a>
              <a href="https://anpc.ro/ce-este-sal" target="_blank" rel="noopener noreferrer" className="block">
                <img src="/sal.png" alt="SAL - Soluționarea Alternativă a Litigiilor" className="w-full h-auto hover:opacity-80 transition-opacity rounded" width="764" height="217" />
              </a>
            </div>
          </div>

          {/* Column 3: Company Info */}
          <div>
            <h4 className="font-bold text-lg mb-4">Date Firmă</h4>
            <div className="text-gray-400 text-sm space-y-2">
              <p><strong className="text-white">{companyData.name}</strong></p>
              <p>CUI: {companyData.cui}</p>
              <p>{companyData.address}</p>
              <p>Email: {companyData.email}</p>
              <p>Tel: {companyData.phone}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
          <p>© 2024-2026 cf-extras.ro. Toate drepturile rezervate.</p>
        </div>
      </div>

      {/* Modal Contact */}
      {showContactModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setShowContactModal(false)}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
              {/* Close button */}
              <button
                onClick={() => setShowContactModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Header */}
              <div className="flex items-center mb-6">
                <Building className="w-8 h-8 text-emerald-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Contact</h2>
              </div>

              {/* Content */}
              <div className="space-y-4">
                {/* Company Name */}
                <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                  <p className="font-bold text-gray-900">{companyData.name}</p>
                  <p className="text-sm text-gray-700">CUI: {companyData.cui}</p>
                </div>

                {/* Address */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Adresă</p>
                    <p className="text-gray-700">{companyData.address}</p>
                    <p className="text-gray-700">{companyData.city}, {companyData.county}</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Email</p>
                    <a href={`mailto:${companyData.email}`} className="text-emerald-600 hover:underline">
                      {companyData.email}
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Telefon</p>
                    <a href={`tel:${companyData.phone}`} className="text-emerald-600 hover:underline">
                      {companyData.phone}
                    </a>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  Program: {companyData.schedule}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </footer>
  );
};

export default Footer;
